import { keyframes } from '@emotion/react';
import { Box, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Icon } from '../Icon';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

// Single app-wide toast duration — intentionally not overridable per toast.
const TOAST_DURATION_MS = 10000;

// Countdown line: recedes from the right edge toward the left, in sync with
// the auto-hide timer.
const countdown = keyframes`
  from { width: 100%; }
  to { width: 0%; }
`;

export type CustomToastProps = {
  isOpen: boolean;
  title: string;
  description: string;
  type?: ToastType;
  onClose: () => void;
  position?: {
    top?: string;
    right?: string;
    left?: string;
    bottom?: string;
  };
};

const getToastStyles = (type: ToastType) => {
  switch (type) {
    case 'success':
      return {
        borderColor: '#27AE60',
        iconBg: '#27AE60',
        iconName: 'check' as const,
        iconColor: 'white',
      };
    case 'error':
      return {
        borderColor: '#E74C3C',
        iconBg: '#E74C3C',
        iconName: 'close' as const,
        iconColor: 'white',
      };
    case 'warning':
      return {
        borderColor: '#F39C12',
        iconBg: '#F39C12',
        iconName: 'warning' as const,
        iconColor: 'white',
      };
    case 'info':
      return {
        borderColor: '#3498DB',
        iconBg: '#3498DB',
        iconName: 'info' as const,
        iconColor: 'white',
      };
    default:
      return {
        borderColor: '#27AE60',
        iconBg: '#27AE60',
        iconName: 'check' as const,
        iconColor: 'white',
      };
  }
};

export const CustomToast: React.FC<CustomToastProps> = ({
  isOpen,
  title,
  description,
  type = 'success',
  onClose,
  position = { top: '112px', right: '24px' },
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Delay to ensure the hidden state renders first, enabling the slide-in animation
      const showTimer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });

      const hideTimer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }, TOAST_DURATION_MS);

      return () => {
        cancelAnimationFrame(showTimer);
        clearTimeout(hideTimer);
      };
    } else {
      setIsVisible(false);
    }
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const styles = getToastStyles(type);

  return (
    <Box
      position="fixed"
      top={position.top}
      right={{ base: '16px', md: position.right }}
      left={{ base: '16px', md: position.left ?? 'unset' }}
      bottom={position.bottom}
      zIndex={9999}
      bg="surface.panel"
      border={`1px solid ${styles.borderColor}`}
      borderRadius="12px"
      padding="16px"
      minWidth={{ base: 'auto', md: '320px' }}
      maxWidth="500px"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
      overflow="hidden"
      display="flex"
      gap={3}
      opacity={isVisible ? 1 : 0}
      transform={isVisible ? 'translateX(0)' : 'translateX(100%)'}
      transition="all 0.3s ease-in-out">
      <Box
        w="20px"
        h="20px"
        bg={styles.iconBg}
        borderRadius="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}>
        <Icon iconName={styles.iconName} boxSize={3} color={styles.iconColor} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Text
          fontSize="14px"
          fontWeight="600"
          color="content.body"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis">
          {title}
        </Text>
        <Text fontSize="13px" fontWeight="400" color="content.muted" mt={1}>
          {description}
        </Text>
      </Box>
      <Box
        cursor="pointer"
        onClick={() => {
          setIsVisible(false);
          setTimeout(onClose, 300);
        }}
        color="content.body"
        fontSize="16px"
        fontWeight="bold"
        flexShrink={0}
        _hover={{ color: 'content.title' }}
        transition="color 0.2s ease">
        ×
      </Box>
      <Box
        position="absolute"
        bottom={0}
        left={0}
        h="3px"
        bg={styles.borderColor}
        animation={`${countdown} ${TOAST_DURATION_MS}ms linear forwards`}
      />
    </Box>
  );
};
